/* eslint-disable react/prop-types */
import React from "react";
import { renderToString } from "react-dom/server";
import { ApolloProvider } from "@apollo/react-hooks";
import { getDataFromTree } from "@apollo/react-ssr";
import delay from "@insightfulscience/shared-utils/async/delay";
import { compose } from "@insightfulscience/shared-utils/fn";
import { renderContext } from "@insightfulscience/smart-react/isomorphic-effect";
import withGetInitialProps from "../utils/withGetInitialProps";
import apolloClient from ".";

const createPageProps = apollo => ({ pageProps }) => ({
	pageProps: {
		...pageProps,
		preloadedState: apollo.extract(),
		apolloContainer: {
			apollo,
			toJSON: () => null,
		},
	},
});

const withApollo = App => {
	const AppWithApollo = ({ Component, pageProps }) => {
		const { preloadedState, apolloContainer, ...props } = pageProps;
		const { apollo } = apolloContainer || {};

		return (
			<ApolloProvider client={apollo || apolloClient({ preloadedState })}>
				<App Component={Component} pageProps={props} />
			</ApolloProvider>
		);
	};

	/**
	 * @todo `getDataFromTree` is not stable with useQuery, so we need to write
	 *        own function that expects all request end before respond
	 */
	const renderApp = (Component, client) => props =>
		getDataFromTree(
			<renderContext.Provider value>
				<App Component={Component} {...props} />
			</renderContext.Provider>,
			{ client, renderFunction: renderToString }, // context
		)
			.then(delay)
			.then(() => props);

	AppWithApollo.getInitialProps = appContext => {
		const { Component, ctx } = appContext;
		const apollo = apolloClient(ctx);
		ctx.apollo = apollo; // eslint-disable-line no-param-reassign

		return App.getInitialProps(appContext)
			.then(renderApp(Component, apollo))
			.then(createPageProps(apollo));
	};

	AppWithApollo.displayName = `WithApollo(${App.displayName || App.name})`;

	return AppWithApollo;
};

export default compose(withApollo, withGetInitialProps);
